import { useEffect, useRef } from 'react';
import type React from 'react';
import type { JSX } from 'react';
import { DARK_THEME, LIGHT_THEME, Renderer } from '@bw/render-2d';
import { EditorController } from '../editor/controller.js';
import { useEditor } from '../state/store.js';
import { TOOL_META } from '../state/tools.js';

export interface CanvasStageProps {
  readonly onReady: (controller: EditorController, renderer: Renderer) => void;
  readonly dark: boolean;
  readonly onCanvasKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
}

/**
 * The two stacked canvases (design document B4). React mounts them and then
 * stays out of the way: the renderer's animation loop pulls its scene from the
 * controller, so pointer motion never re-renders a component.
 */
export function CanvasStage({ onReady, dark, onCanvasKeyDown }: CanvasStageProps): JSX.Element {
  const wallRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const tool = useEditor((state) => state.tool);

  useEffect(() => {
    const wall = wallRef.current;
    const overlay = overlayRef.current;
    const surface = surfaceRef.current;
    if (wall === null || overlay === null || surface === null) return;

    const renderer = new Renderer(wall, overlay);
    rendererRef.current = renderer;
    const controller = new EditorController(renderer);
    const detach = controller.attach(surface);

    const resize = (): void => {
      const rect = surface.getBoundingClientRect();
      renderer.resize(rect.width, rect.height, window.devicePixelRatio || 1);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(surface);
    const onDpr = (): void => resize();
    window.addEventListener('resize', onDpr);

    renderer.start();
    onReady(controller, renderer);

    return () => {
      // Synchronous and import-free: the bridge carries its own detach, so a
      // teardown can never land after the next mount has attached.
      globalThis.__bw?.detach?.(controller);
      renderer.stop();
      observer.disconnect();
      window.removeEventListener('resize', onDpr);
      detach();
      rendererRef.current = null;
    };
  }, [onReady]);

  useEffect(() => {
    rendererRef.current?.setTheme(dark ? DARK_THEME : LIGHT_THEME);
  }, [dark]);

  return (
    <div className="stage">
      <canvas ref={wallRef} />
      <canvas ref={overlayRef} className="overlay" />
      <div
        ref={surfaceRef}
        className="surface"
        style={{ cursor: TOOL_META[tool].cursor }}
        tabIndex={0}
        onKeyDown={onCanvasKeyDown}
        role="application"
        aria-label="Wall drawing surface. Arrow keys move the preview, Enter lays a brick."
      />
    </div>
  );
}
